import { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Select,
  Typography,
  Space,
  Alert,
  Modal,
  Table,
  Avatar,
  Tabs,
} from "antd";
import { Api, NotificationType } from "@model";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Title, Text } = Typography;

const EMAIL_FROM_OPTIONS = [
  "support@lingua-talk.com",
  "noreply@lingua-talk.com",
  "info@lingua-talk.com",
];

interface MatchingUser {
  _id: string;
  name?: string;
  email?: string;
  installedVersion?: string;
}

interface SurveyOption {
  _id: string;
  title: string;
  isActive?: boolean;
}

const NotificationPage = () => {
  const [form] = Form.useForm();
  const [formEmail] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [conditionError, setConditionError] = useState<string | null>(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [matchingUsers, setMatchingUsers] = useState<MatchingUser[]>([]);
  const [testLoading, setTestLoading] = useState(false);

  const [surveys, setSurveys] = useState<SurveyOption[]>([]);
  const [emailUserCount, setEmailUserCount] = useState<number | null>(null);
  const [emailConditionError, setEmailConditionError] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    Api.get("/api/admin/panel/surveys", { params: { page: 1, limit: 200 } })
      .then((response) => {
        const items = response?.data?.data?.data ?? [];
        setSurveys(
          items.filter((survey: SurveyOption) => survey.isActive !== false),
        );
      })
      .catch(() => setSurveys([]));
  }, []);

  const handleEmailConditionChange = async (value: string) => {
    setEmailConditionError(null);
    setEmailUserCount(null);
    if (!value?.trim()) return;
    try {
      const condition = JSON.parse(value);
      const response = await Api.post(
        "/api/admin/panel/notifications/count-users-email",
        { condition }
      );
      if (response?.data?.data?.count !== undefined) {
        setEmailUserCount(response.data.data.count);
      }
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setEmailConditionError("Invalid JSON format");
      } else {
        setEmailConditionError(
          err?.response?.data?.message || "Failed to count users"
        );
      }
    }
  };

  const handleTestEmailCondition = async () => {
    try {
      const values = formEmail.getFieldsValue();
      if (!values.condition) {
        message.warning("Please enter a condition first");
        return;
      }
      let condition: any;
      try {
        condition = JSON.parse(values.condition);
      } catch {
        message.error("Invalid JSON format in condition field");
        return;
      }
      setTestLoading(true);
      const response = await Api.post(
        "/api/admin/panel/notifications/list-users-email",
        { condition, limit: 100 }
      );
      if (response?.data?.data?.users) {
        setMatchingUsers(response.data.data.users);
        setTestModalVisible(true);
      } else {
        message.error("Failed to fetch matching users");
      }
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Failed to test condition"
      );
    } finally {
      setTestLoading(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      const values = await formEmail.validateFields();
      let condition: any;
      try {
        condition = JSON.parse(values.condition);
      } catch {
        message.error("Invalid JSON format in condition field");
        return;
      }
      Modal.confirm({
        title: "Confirm Send Email",
        icon: <ExclamationCircleOutlined />,
        width: 560,
        content: (
          <div style={{ marginTop: 16 }}>
            <Text strong>Recipients: </Text>
            <Text style={{ color: "#1890ff" }}>{emailUserCount ?? 0} user(s)</Text>
            <div style={{ background: "#f5f5f5", padding: 16, borderRadius: 4, marginTop: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>From: </Text>
                <Text>{values.fromEmail}</Text>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Title: </Text>
                <Text>{values.title}</Text>
              </div>
              <div>
                <Text strong>Body: </Text>
                <Text>{values.body}</Text>
              </div>
            </div>
            <Text type="secondary">Are you sure you want to send this email?</Text>
          </div>
        ),
        okText: "Send",
        okType: "primary",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            setEmailLoading(true);
            const response = await Api.post(
              "/api/admin/panel/notifications/send-email",
              {
                condition,
                title: values.title,
                body: values.body,
                fromEmail: values.fromEmail,
                password: values.password,
              }
            );
            if (response?.data?.data?.success) {
              message.success(
                `Email sent successfully to ${response.data.data.count ?? 0} users`
              );
              formEmail.resetFields();
              setEmailUserCount(null);
            } else {
              message.error(
                response?.data?.data?.message || "Failed to send email"
              );
            }
          } catch (err: any) {
            if (err.errorFields) return;
            message.error(err?.response?.data?.message || "Failed to send email");
          } finally {
            setEmailLoading(false);
          }
        },
      });
    } catch (err: any) {
      if (err.errorFields) return;
      message.error("Please fill all required fields");
    }
  };

  const handleConditionChange = async (value: string) => {
    setConditionError(null);
    setUserCount(null);

    if (!value || value.trim() === "") {
      return;
    }

    try {
      const condition = JSON.parse(value);
      const response = await Api.post(
        "/api/admin/panel/notifications/count-users",
        {
          condition,
        }
      );
      if (response?.data?.data?.count !== undefined) {
        setUserCount(response.data.data.count);
      }
    } catch (error: any) {
      if (error instanceof SyntaxError) {
        setConditionError("Invalid JSON format");
      } else {
        setConditionError(
          error?.response?.data?.message || "Failed to count users"
        );
      }
    }
  };

  const handleTestCondition = async () => {
    try {
      const values = form.getFieldsValue();
      if (!values.condition) {
        message.warning("Please enter a condition first");
        return;
      }

      let condition;
      try {
        condition = JSON.parse(values.condition);
      } catch (e) {
        message.error("Invalid JSON format in condition field");
        return;
      }

      setTestLoading(true);
      const response = await Api.post(
        "/api/admin/panel/notifications/list-users",
        {
          condition,
          limit: 100,
        }
      );

      if (response?.data?.data?.users) {
        setMatchingUsers(response.data.data.users);
        setTestModalVisible(true);
      } else {
        message.error("Failed to fetch matching users");
      }
    } catch (error: any) {
      console.error("Error testing condition:", error);
      message.error(
        error?.response?.data?.message || "Failed to test condition"
      );
    } finally {
      setTestLoading(false);
    }
  };

  const handleSendNotification = async () => {
    try {
      const values = await form.validateFields();

      let condition: any;
      try {
        condition = JSON.parse(values.condition);
      } catch (e) {
        message.error("Invalid JSON format in condition field");
        return;
      }

      const getNotificationTypeLabel = (type: string) => {
        const labels: Record<string, string> = {
          [NotificationType.FOLLOW_REQUEST]: "Follow Request",
          [NotificationType.FOLLOW_ACCEPT]: "Follow Accept",
          [NotificationType.PARTNER_SUGGESTION]: "Partner Suggestion",
          [NotificationType.MESSAGE]: "Message",
          [NotificationType.OTHER]: "Other",
          [NotificationType.SURVEY]: "Survey",
          [NotificationType.IS_TYPING]: "Is Typing",
          [NotificationType.SEEN]: "Seen",
          [NotificationType.LOGOUT]: "Logout",
        };
        return labels[type] || type;
      };

      Modal.confirm({
        title: "Confirm Send Notification",
        icon: <ExclamationCircleOutlined />,
        width: 600,
        content: (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Recipients: </Text>
              <Text style={{ color: "#1890ff" }}>{userCount || 0} user(s)</Text>
            </div>
            <div
              style={{
                background: "#f5f5f5",
                padding: 16,
                borderRadius: 4,
                marginBottom: 16,
              }}
            >
              {values.notificationType === NotificationType.SURVEY ? (
                <div style={{ marginBottom: 12 }}>
                  <Text>
                    Each user receives this survey’s title and description in their origin language.
                  </Text>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Title:
                    </Text>
                    <Text>{values.title}</Text>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Body:
                    </Text>
                    <Text>{values.body}</Text>
                  </div>
                </>
              )}
              <div style={{ marginBottom: values.notificationType === NotificationType.SURVEY ? 12 : 0 }}>
                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  Type:
                </Text>
                <Text>
                  {getNotificationTypeLabel(
                    values.notificationType || NotificationType.OTHER
                  )}
                </Text>
              </div>
              {values.notificationType === NotificationType.SURVEY && (
                <div>
                  <Text strong style={{ display: "block", marginBottom: 4 }}>
                    Survey:
                  </Text>
                  <Text>
                    {surveys.find((survey) => survey._id === values.surveyId)?.title ||
                      values.surveyId}
                  </Text>
                </div>
              )}
            </div>
            <Text type="secondary">
              Are you sure you want to send this notification?
            </Text>
          </div>
        ),
        okText: "Send",
        okType: "primary",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            setLoading(true);
            const isSurvey = values.notificationType === NotificationType.SURVEY;
            const response = await Api.post(
              "/api/admin/panel/notifications/send-group",
              {
                condition,
                title: isSurvey ? undefined : values.title,
                body: isSurvey ? undefined : values.body,
                notificationType:
                  values.notificationType || NotificationType.OTHER,
                surveyId: values.surveyId,
                password: values.password,
              }
            );

            if (response?.data?.data?.success) {
              const invalid = response.data.data.invalidTokenCount;
              message.success(
                invalid != null && invalid > 0
                  ? `Sent to ${response.data.data.count || 0} users; ${invalid} invalid token(s) marked as FCM expired.`
                  : `Notification sent successfully to ${
                      response.data.data.count || 0
                    } users`
              );
              form.resetFields();
              form.setFieldsValue({ notificationType: NotificationType.OTHER });
              setUserCount(null);
            } else {
              message.error(
                response?.data?.data?.message || "Failed to send notification"
              );
            }
          } catch (error: any) {
            console.error("Error sending notification:", error);
            if (error.errorFields) {
              return;
            }
            message.error(
              error?.response?.data?.message || "Failed to send notification"
            );
          } finally {
            setLoading(false);
          }
        },
      });
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error("Please fill all required fields");
    }
  };

  return (
    <Card style={{ margin: "20px", maxWidth: "800px" }}>
      <Title level={2}>Group Notification</Title>

      <Tabs defaultActiveKey="notification" style={{ marginTop: 16 }}>
        <Tabs.TabPane key="notification" tab="Notification">
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 24 }}
        onFinish={handleSendNotification}
      >
        <Form.Item
          name="condition"
          label="Condition (JSON)"
          rules={[
            { required: true, message: "Please enter condition" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                try {
                  JSON.parse(value);
                  return Promise.resolve();
                } catch (e) {
                  return Promise.reject(new Error("Invalid JSON format"));
                }
              },
            },
          ]}
          help={
            <div>
              {userCount !== null && (
                <div style={{ marginTop: 8 }}>
                  <Text strong style={{ color: "#1890ff" }}>
                    {userCount} user(s) match this condition
                  </Text>
                </div>
              )}
              {conditionError && (
                <Alert
                  message={conditionError}
                  type="error"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
          }
        >
          <TextArea
            rows={4}
            placeholder="{ ... }"
            onChange={(e) => {
              form.setFieldsValue({ condition: e.target.value });
              handleConditionChange(e.target.value);
            }}
          />
        </Form.Item>

        <Form.Item
          name="notificationType"
          label="Notification Type"
          initialValue={NotificationType.OTHER}
        >
          <Select placeholder="Select notification type">
            <Select.Option value={NotificationType.FOLLOW_REQUEST}>
              Follow Request
            </Select.Option>
            <Select.Option value={NotificationType.FOLLOW_ACCEPT}>
              Follow Accept
            </Select.Option>
            <Select.Option value={NotificationType.PARTNER_SUGGESTION}>
              Partner Suggestion
            </Select.Option>
            <Select.Option value={NotificationType.MESSAGE}>
              Message
            </Select.Option>
            <Select.Option value={NotificationType.OTHER}>Other</Select.Option>
            <Select.Option value={NotificationType.SURVEY}>Survey</Select.Option>
            <Select.Option value={NotificationType.IS_TYPING}>
              Is Typing
            </Select.Option>
            <Select.Option value={NotificationType.SEEN}>Seen</Select.Option>
            <Select.Option value={NotificationType.LOGOUT}>
              Logout
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, next) => prev.notificationType !== next.notificationType}
        >
          {() =>
            form.getFieldValue("notificationType") === NotificationType.SURVEY ? (
              <Form.Item
                name="surveyId"
                label="Survey"
                rules={[{ required: true, message: "Please select a survey" }]}
              >
                {surveys.length > 0 ? (
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder="Select a survey"
                  >
                    {surveys.map((survey) => (
                      <Select.Option key={survey._id} value={survey._id}>
                        {survey.title} ({survey._id})
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Input placeholder="Survey id" />
                )}
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, next) => prev.notificationType !== next.notificationType}
        >
          {() =>
            form.getFieldValue("notificationType") === NotificationType.SURVEY ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Title and description come from the survey, in each user’s origin language."
              />
            ) : (
              <>
                <Form.Item
                  name="title"
                  label="Notification Title"
                  dependencies={["notificationType"]}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (form.getFieldValue("notificationType") === NotificationType.SEEN)
                          return Promise.resolve();
                        if (!value || !String(value).trim())
                          return Promise.reject(new Error("Please enter notification title"));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="Enter notification title" />
                </Form.Item>

                <Form.Item
                  name="body"
                  label="Notification Body"
                  dependencies={["notificationType"]}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (form.getFieldValue("notificationType") === NotificationType.SEEN)
                          return Promise.resolve();
                        if (!value || !String(value).trim())
                          return Promise.reject(new Error("Please enter notification body"));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <TextArea rows={4} placeholder="Enter notification body" />
                </Form.Item>
              </>
            )
          }
        </Form.Item>

        <Form.Item
          name="password"
          label="Admin Password"
          rules={[
            { required: true, message: "Please enter your admin password" },
          ]}
        >
          <Input.Password placeholder="Enter your admin password" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={userCount === null || userCount === 0}
            >
              Send Notification
            </Button>
            <Button
              onClick={handleTestCondition}
              loading={testLoading}
              disabled={userCount === null || userCount === 0}
            >
              Test
            </Button>
            <Button onClick={() => form.resetFields()}>Reset</Button>
          </Space>
        </Form.Item>
      </Form>
        </Tabs.TabPane>

        <Tabs.TabPane key="email" tab="Email">
          <Form
            form={formEmail}
            layout="vertical"
            style={{ marginTop: 24 }}
            onFinish={handleSendEmail}
          >
            <Form.Item
              name="condition"
              label="Condition (JSON)"
              rules={[
                { required: true, message: "Please enter condition" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    try {
                      JSON.parse(value);
                      return Promise.resolve();
                    } catch {
                      return Promise.reject(new Error("Invalid JSON format"));
                    }
                  },
                },
              ]}
              help={
                <div>
                  {emailUserCount !== null && (
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ color: "#1890ff" }}>
                        {emailUserCount} user(s) match this condition
                      </Text>
                    </div>
                  )}
                  {emailConditionError && (
                    <Alert
                      message={emailConditionError}
                      type="error"
                      showIcon
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              }
            >
              <TextArea
                rows={4}
                placeholder="{ ... }"
                onChange={(e) => {
                  formEmail.setFieldsValue({ condition: e.target.value });
                  handleEmailConditionChange(e.target.value);
                }}
              />
            </Form.Item>
            <Form.Item
              name="fromEmail"
              label="From"
              rules={[{ required: true, message: "Please select from email" }]}
            >
              <Select
                placeholder="Select from email"
                options={EMAIL_FROM_OPTIONS.map((e) => ({ value: e, label: e }))}
              />
            </Form.Item>
            <Form.Item
              name="title"
              label="Email Title"
              rules={[{ required: true, message: "Please enter title" }]}
            >
              <Input placeholder="Enter email title" />
            </Form.Item>
            <Form.Item
              name="body"
              label="Email Body"
              rules={[{ required: true, message: "Please enter body" }]}
            >
              <TextArea rows={4} placeholder="Enter email body" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Admin Password"
              rules={[{ required: true, message: "Please enter your admin password" }]}
            >
              <Input.Password placeholder="Enter your admin password" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={emailLoading}
                  disabled={emailUserCount === null || emailUserCount === 0}
                >
                  Send Email
                </Button>
                <Button
                  onClick={handleTestEmailCondition}
                  loading={testLoading}
                  disabled={emailUserCount === null || emailUserCount === 0}
                >
                  Test
                </Button>
                <Button onClick={() => { formEmail.resetFields(); setEmailUserCount(null); setEmailConditionError(null); }}>Reset</Button>
              </Space>
            </Form.Item>
          </Form>
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title={`Matching Users (${matchingUsers.length})`}
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTestModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <Table
          dataSource={matchingUsers}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: "Name",
              dataIndex: "name",
              key: "name",
              render: (name: string, record: MatchingUser) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar>
                    {name?.[0]?.toUpperCase() ||
                      record.email?.[0]?.toUpperCase() ||
                      "?"}
                  </Avatar>
                  <span>{name || record.email || record._id}</span>
                </div>
              ),
            },
            {
              title: "Email",
              dataIndex: "email",
              key: "email",
              render: (email: string) => email || "-",
            },
            {
              title: "Version",
              dataIndex: "installedVersion",
              key: "installedVersion",
              render: (version: string) => version || "-",
            },
          ]}
        />
      </Modal>
    </Card>
  );
};

export default NotificationPage;
